<template>
  <div class="p-6">
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">辞书书籍管理</h1>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理辞书书籍的基本信息和分类</p>
    </div>

    <!-- 筛选器和操作按钮 -->
    <div class="mb-4 space-y-4">
      <!-- 搜索和操作 -->
      <div class="flex gap-4">
        <div class="flex-1">
          <input v-model="searchKeyword" type="text" placeholder="搜索书籍标题或描述..."
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            @input="handleSearch" />
        </div>
        <div class="flex items-center gap-2">
          <button v-if="selectedIds.length > 0" @click="handleBatchDelete"
            class="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            批量删除 ({{ selectedIds.length }})
          </button>
          <button @click="loadBooks"
            class="cancel-btn">
            刷新
          </button>
          <button @click="handleCreate"
            class="primary-btn gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            新增书籍
          </button>
        </div>
      </div>

      <!-- 筛选器 -->
      <div class="flex gap-4">
        <select v-model="filters.category_id"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange">
          <option :value="null">全部分类</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
        <select v-model="filters.difficulty_level"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange">
          <option :value="null">全部难度</option>
          <option :value="1">★☆☆☆☆ (入门)</option>
          <option :value="2">★★☆☆☆ (初级)</option>
          <option :value="3">★★★☆☆ (中级)</option>
          <option :value="4">★★★★☆ (高级)</option>
          <option :value="5">★★★★★ (专家)</option>
        </select>
        <select v-model="filters.is_public"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange">
          <option :value="null">全部状态</option>
          <option :value="true">公开</option>
          <option :value="false">私有</option>
        </select>
        <select v-model="filters.is_free"
          class="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @change="handleFilterChange">
          <option :value="null">全部类型</option>
          <option :value="true">免费</option>
          <option :value="false">付费</option>
        </select>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 书籍列表 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-hidden">
      <BookTable :books="books" :selectable="true" v-model:selected-ids="selectedIds" @view="handleView"
        @edit="handleEdit" @delete="handleDelete" />

      <!-- 分页 -->
      <Pagination v-if="total > 0" :total="total" v-model:current-page="currentPage" v-model:page-size="pageSize"
        @update:current-page="loadBooks" @update:page-size="loadBooks" />
    </div>

    <!-- 创建/编辑书籍模态框 -->
    <Modal :show="showModal" @close="handleCancel">
      <template #header>
        {{ editingBook ? '编辑书籍' : '新增书籍' }}
      </template>
      <template #body>
        <BookForm :initial-data="formData" :categories="categories" :loading="submitting" @submit="handleSubmit"
          @cancel="handleCancel" />
      </template>
    </Modal>


    <!-- 确认删除对话框 -->
    <ConfirmDialog :show="showDeleteConfirm" type="danger" title="确认删除"
      :message="`确定要删除书籍「${deletingBook?.title}」吗？此操作不可恢复。`" confirm-text="删除" cancel-text="取消" @confirm="confirmDelete"
      @cancel="() => showDeleteConfirm = false" />

    <!-- 批量删除确认对话框 -->
    <ConfirmDialog :show="showBatchDeleteConfirm" type="danger" title="确认批量删除"
      :message="`确定要删除选中的 ${selectedIds.length} 本书籍吗？此操作不可恢复。`" confirm-text="删除" cancel-text="取消"
      @confirm="confirmBatchDelete" @cancel="() => showBatchDeleteConfirm = false" />
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import BookTable from '@/components/dictation/BookTable.vue';
import BookForm from '@/components/dictation/BookForm.vue';
import Modal from '@/components/ui/Modal.vue';
import Pagination from '@/components/ui/Pagination.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import { getBookListAPI, createBookAPI, updateBookAPI, deleteBookAPI } from '@/api/dictation/book';
import { getCategoryListAPI } from '@/api/dictation/category';
import type { Book } from '@/types/dictation/book';
import type { CategoryWithSubCategories } from '@/types/dictation/category';

/**
 * 辞书书籍管理页面
 */

const router = useRouter();

// 数据状态
const loading = ref(false);
const books = ref<Book[]>([]);
const categories = ref<CategoryWithSubCategories[]>([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(20);
const searchKeyword = ref('');
const selectedIds = ref<number[]>([]);

// 筛选器
const filters = ref({
  category_id: null as number | null,
  difficulty_level: null as number | null,
  is_public: null as boolean | null,
  is_free: null as boolean | null,
});

// 模态框状态
const showModal = ref(false);
const showDeleteConfirm = ref(false);
const showBatchDeleteConfirm = ref(false);
const submitting = ref(false);
const editingBook = ref<Book | null>(null);
const deletingBook = ref<Book | null>(null);

// 表单数据
const formData = ref({
  title: '',
  description: '',
  category_id: null as number | null,
  subcategory_id: null as number | null,
  language_id: 1,
  difficulty_level: 1,
  is_public: true,
  is_free: true,
});


/**
 * 加载书籍列表
 */
const loadBooks = async () => {
  loading.value = true;
  try {
    const params: any = {
      page: currentPage.value,
      limit: pageSize.value,
    };

    if (searchKeyword.value) {
      params.search = searchKeyword.value;
    }

    if (filters.value.category_id) {
      params.category_id = filters.value.category_id;
    }

    if (filters.value.difficulty_level) {
      params.difficulty_level = filters.value.difficulty_level;
    }

    if (filters.value.is_public !== null) {
      params.is_public = filters.value.is_public;
    }

    if (filters.value.is_free !== null) {
      params.is_free = filters.value.is_free;
    }

    const response = await getBookListAPI(params);
    books.value = response.data?.books || [];
    total.value = response.data?.total || 0;
  } catch (error) {
    console.error('加载书籍列表失败:', error);
    alert('加载书籍列表失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

/**
 * 加载分类列表
 */
const loadCategories = async () => {
  try {
    const response = await getCategoryListAPI(true, true);
    categories.value = response.data?.categories || [];
  } catch (error) {
    console.error('加载分类列表失败:', error);
  }
};

/**
 * 搜索处理
 */
let searchTimer: ReturnType<typeof setTimeout> | null = null;
const handleSearch = () => {
  if (searchTimer) {
    clearTimeout(searchTimer);
  }
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    loadBooks();
  }, 500);
};

/**
 * 筛选器变化处理
 */
const handleFilterChange = () => {
  currentPage.value = 1;
  loadBooks();
};



/**
 * 查看书籍详情
 */
const handleView = (book: Book) => {
  router.push(`/dictation/books/${book.id}`);
};

/**
 * 创建书籍
 */
const handleCreate = () => {
  editingBook.value = null;
  formData.value = {
    title: '',
    description: '',
    category_id: null,
    subcategory_id: null,
    language_id: 1,
    difficulty_level: 1,
    is_public: true,
    is_free: true,
  };
  showModal.value = true;
};

/**
 * 编辑书籍
 */
const handleEdit = (book: Book) => {
  editingBook.value = book;
  formData.value = {
    title: book.title,
    description: book.description || '',
    category_id: book.categoryId || null,
    subcategory_id: book.subcategoryId || null,
    language_id: book.languageId,
    difficulty_level: book.difficultyLevel,
    is_public: book.isPublic,
    is_free: book.isFree,
  };
  showModal.value = true;
};

/**
 * 提交表单
 */
const handleSubmit = async (data: any) => {
  submitting.value = true;
  try {
    if (editingBook.value) {
      await updateBookAPI(editingBook.value.id, data);
      alert('更新成功');
    } else {
      await createBookAPI(data);
      alert('创建成功');
    }
    showModal.value = false;
    loadBooks();
  } catch (error) {
    console.error('保存失败:', error);
    alert('保存失败，请稍后重试');
  } finally {
    submitting.value = false;
  }
};

/**
 * 取消编辑
 */
const handleCancel = () => {
  showModal.value = false;
};

/**
 * 删除书籍
 */
const handleDelete = (book: Book) => {
  deletingBook.value = book;
  showDeleteConfirm.value = true;
};

/**
 * 确认删除
 */
const confirmDelete = async () => {
  if (!deletingBook.value) return;

  try {
    await deleteBookAPI(deletingBook.value.id);
    alert('删除成功');
    showDeleteConfirm.value = false;
    loadBooks();
  } catch (error) {
    console.error('删除失败:', error);
    alert('删除失败，请稍后重试');
  }
};

/**
 * 批量删除
 */
const handleBatchDelete = () => {
  showBatchDeleteConfirm.value = true;
};

/**
 * 确认批量删除
 */
const confirmBatchDelete = async () => {
  try {
    await Promise.all(selectedIds.value.map(id => deleteBookAPI(id)));
    alert('批量删除成功');
    showBatchDeleteConfirm.value = false;
    selectedIds.value = [];
    loadBooks();
  } catch (error) {
    console.error('批量删除失败:', error);
    alert('批量删除失败，请稍后重试');
  }
};

// 初始化
onMounted(() => {
  loadBooks();
  loadCategories();
});
</script>
